import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Calendar.module.css';

/**
 * 달력 — 날짜 하나를 고릅니다.
 *
 * 일정 입력 · 모임 수정 · 다시 만들기 등 여러 화면이 같은 달력을 씁니다.
 * 화면마다 각자 그리면 지난 날짜 처리와 주말 색이 갈라지므로 여기 하나로 둡니다.
 *
 * 고른 날짜는 색이 아니라 검은 판으로 보여 줍니다(색만으로 구분하지 않기).
 */

const IcPrev = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const IcNext = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const pad = (n: number) => String(n).padStart(2, '0');
/** Date → 'YYYY-MM-DD' (로컬 기준. toISOString 은 UTC 로 밀려 하루 어긋납니다) */
export const toDateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

interface CalendarProps {
    /** 고른 날짜 'YYYY-MM-DD' */
    value?: string;
    onChange: (dateKey: string, date: Date) => void;
    /** 이 날짜보다 이전은 고를 수 없습니다 (기본: 오늘) */
    min?: Date | null;
    /** 이 날짜보다 이후는 고를 수 없습니다 */
    max?: Date | null;
}

export const Calendar = ({ value, onChange, min, max }: CalendarProps) => {
    const { t } = useTranslation();
    const weekDays = (() => {
        const raw = t('common.weekDaysShort', { returnObjects: true }) as unknown;
        return Array.isArray(raw) ? raw as string[] : ['일', '월', '화', '수', '목', '금', '토'];
    })();

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const floor = min === null ? null : (min || today);

    const [month, setMonth] = useState(() => {
        const base = value ? new Date(value) : new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });

    const year = month.getFullYear();
    const mon = month.getMonth();
    const daysInMonth = new Date(year, mon + 1, 0).getDate();
    const firstWeekday = new Date(year, mon, 1).getDay();

    return (
        <div className={styles.wrap}>
            <div className={styles.head}>
                <button
                    type="button"
                    className={styles.nav}
                    onClick={() => setMonth(new Date(year, mon - 1, 1))}
                    aria-label={t('common.prev')}
                >
                    <IcPrev />
                </button>
                <span className={styles.month}>{t('common.yearMonth', { year, month: mon + 1 })}</span>
                <button
                    type="button"
                    className={styles.nav}
                    onClick={() => setMonth(new Date(year, mon + 1, 1))}
                    aria-label={t('common.next')}
                >
                    <IcNext />
                </button>
            </div>

            <div className={styles.grid}>
                {weekDays.map((d, i) => (
                    <span
                        key={d}
                        className={[
                            styles.weekday,
                            i === 0 ? styles.sun : '',
                            i === 6 ? styles.sat : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {d}
                    </span>
                ))}

                {Array.from({ length: firstWeekday }, (_, i) => (
                    <span key={`pad-${i}`} />
                ))}

                {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(year, mon, day);
                    const key = toDateKey(date);
                    const disabled = (floor != null && date < floor) || (max != null && date > max);
                    const selected = value === key;
                    const isToday = key === toDateKey(today);
                    const weekday = date.getDay();
                    return (
                        <button
                            key={key}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(key, date)}
                            className={[
                                styles.day,
                                selected ? styles.daySelected : '',
                                isToday && !selected ? styles.dayToday : '',
                                weekday === 0 ? styles.sun : '',
                                weekday === 6 ? styles.sat : '',
                            ].filter(Boolean).join(' ')}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
